// public/js/features/reports/api.js
// Thin client for the data-export add-on endpoints (docs/data-export.md).
import { authenticatedFetch } from '../../modules/auth.js';

async function j(res) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

const jsonOpts = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export async function listReports(tenantId) {
  const q = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return j(await authenticatedFetch(`api/v1/reports${q}`));
}

export async function createReport(def) {
  return j(await authenticatedFetch('api/v1/reports', jsonOpts('POST', def)));
}

export async function deleteReport(id) {
  return j(await authenticatedFetch(`api/v1/reports/${encodeURIComponent(id)}`, { method: 'DELETE' }));
}

export async function runReport(id, format) {
  return j(await authenticatedFetch(`api/v1/reports/${encodeURIComponent(id)}/run`, jsonOpts('POST', { format })));
}

export async function runStatus(runId) {
  return j(await authenticatedFetch(`api/v1/reports/runs/${encodeURIComponent(runId)}`));
}

// Fetch the finished file (auth + entitlement enforced server-side) and save it.
export async function downloadRun(runId, filename) {
  const res = await authenticatedFetch(`api/v1/reports/runs/${encodeURIComponent(runId)}/file`);
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'report';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export async function loadMyTenants() {
  return j(await authenticatedFetch('api/v1/my-tenants'));
}

// Platform-only: grant/revoke a tenant add-on entitlement.
// payload: { addon:'dataExport', enabled, expiresAt?, note? }
export async function setTenantAddon(tenantId, payload) {
  return j(await authenticatedFetch(`api/v1/admin/tenants/${encodeURIComponent(tenantId)}/addons`, jsonOpts('POST', payload)));
}

// One-shot inline export (on-page Download menus): fire an ad-hoc report for one
// asset + subject + window, wait for it, and save the file. Same pipeline and
// entitlement as the Reports page. Throws on failure.
export async function quickExport({ subject, assetUUID, from, to, format = 'csv', name }) {
  const { runId } = await j(await authenticatedFetch('api/v1/reports/adhoc',
    jsonOpts('POST', { subject, assetUUID, from, to, format, name })));
  const run = await waitForRun(runId);
  await downloadRun(runId, run.filename);
  return run;
}

// Poll a run to completion. onTick(status) is called each poll. Resolves with the
// final run object (ready) or throws on failed/timeout.
export async function waitForRun(runId, { onTick, intervalMs = 1500, timeoutMs = 120000 } = {}) {
  const start = Date.now();
  for (;;) {
    const { run } = await runStatus(runId);
    onTick?.(run);
    if (run.status === 'ready') return run;
    if (run.status === 'failed') throw new Error(run.error || 'Report generation failed');
    if (Date.now() - start > timeoutMs) throw new Error('Timed out waiting for the report');
    await new Promise(r => setTimeout(r, intervalMs));
  }
}
